// ─── Tareas que se repiten: dónde cae la siguiente (GHE #683) ───
//
// Toda la decisión de fechas de la recurrencia vive aquí, pura y sin dependencias, por la misma
// razón que el resto de este directorio: el servidor la ejecuta al cerrar una tarea y la ficha la
// usa para decir «nacerá el lunes 13» ANTES de cerrarla. Dos implementaciones de esta regla
// serían dos fechas distintas para la misma promesa.
//
// 🚫 POR QUÉ NO RRULE, que es lo que pedía el cuerpo de la issue («idealmente RFC 5545 RRULE en
// vez de una gramática propia»). Medido sobre la copia de producción el 2026-10-04: de 46 grupos
// de 3+ tareas repetidas a mano, 41 tienen una cadencia clara y **29 de esas 41 son semanales**
// (1 quincenal, 11 con huecos sueltos). RRULE trae parser, zonas horarias, BYSETPOS, BYDAY y
// EXDATE para cubrir un espacio de casos que aquí no tiene ni un solo usuario, y la forma de
// equivocarse es cara: acabas manteniendo un motor de calendario. Tres preajustes y un «hasta»
// cubren las 41 series medidas. El día que aparezca «el último viernes del mes» se añade ese
// caso, no antes.

/** Los tres preajustes. Decisión del propietario 2026-10-04 sobre el RRULE del cuerpo de #683. */
export const TASK_REPEAT_RULES = ['DAILY_WORKDAY', 'WEEKLY', 'MONTHLY'] as const;

export type TaskRepeatRule = (typeof TASK_REPEAT_RULES)[number];

/**
 * ¿Es este valor una de las tres reglas?
 *
 * Vive aquí y no en cada consumidor porque la columna de la base es `NVARCHAR`: lo que sale de
 * ella es `string | null` y TODO el que lo lea —el servidor al planificar, la ficha al pintar el
 * selector— necesita estrecharlo. Dos guardas serían dos listas que se pueden desincronizar, que
 * es exactamente lo que `TASK_REPEAT_RULES` existe para evitar.
 */
export function isTaskRepeatRule(valor: string | null | undefined): valor is TaskRepeatRule {
  return valor != null && (TASK_REPEAT_RULES as readonly string[]).includes(valor);
}

/**
 * Por qué no hay una siguiente ocurrencia, cuando no la hay. Un resultado etiquetado y no un
 * `null` pelado porque los tres motivos piden cosas distintas de quien llama: `series-ended` es la
 * serie terminando como se pidió (no se registra nada), y `no-valid-day` es la regla rindiéndose
 * — ése **sí** merece una línea de log, porque una serie que muere en silencio es exactamente el
 * fallo que esta función existe para no tener.
 */
export type NextOccurrence =
  | { kind: 'next'; date: string }
  | { kind: 'series-ended' }
  | { kind: 'no-valid-day' };

export interface NextOccurrenceInput {
  /**
   * La fecha de la ocurrencia que se acaba de cerrar (`YYYY-MM-DD`), que es su `dueDate` y no su
   * `completedAt`: la serie la definen sus fechas previstas, no el día que alguien se acordó de
   * pulsar el botón. Si fuese `completedAt`, cerrar dos semanas tarde desplazaría la serie entera
   * dos semanas para siempre.
   */
  from: string;
  rule: TaskRepeatRule;
  /** Hoy, en día civil. La siguiente siempre nace en el futuro — ver el bucle de abajo. */
  today: string;
  /** Fin de la serie, inclusive. `null` = indefinida (decisión del propietario 2026-10-04). */
  until: string | null;
  /**
   * El último día para el que `isWorkingDay` puede responder de verdad. Pasado él la función se
   * rinde con `no-valid-day` en vez de seguir preguntando.
   *
   * 🚨 Este parámetro existe para que «no tengo datos» no pueda disfrazarse de «es festivo». Un
   * `isWorkingDay` que devuelve `false` fuera de su rango cargado haría que la serie se saltara
   * días laborables perfectamente normales, y lo haría en silencio. Quien llama lo calcula con
   * {@link recurrenceHorizon} y carga EXACTAMENTE ese rango.
   */
  horizon: string;
  /**
   * Sólo MENSUAL: el día del mes al que pertenece la serie (1-31), tomado de su PRIMERA
   * ocurrencia. Ausente ⇒ el día de `from`.
   *
   * 🚨 Esto es lo que impide que una serie mensual se vaya andando sola, y hay dos formas de que
   * eso pase si no se pasa: el recorte de fin de mes (31 de enero → 28 de febrero, y a partir de
   * ahí el 28 para siempre) y el corrimiento a laborable (el 15 cae en sábado → 17, y a partir de
   * ahí el 17). Las dos mueven el ancla un poco cada vez; en un año la serie se ha desplazado una
   * semana larga y nadie sabe cuándo empezó.
   *
   * Con el ancla, el 15 en sábado nace el 17 ESA VEZ, y el mes siguiente vuelve al 15.
   */
  monthlyAnchorDay?: number;
  /** `true` si ese día se trabaja. Sale del calendario de oficina real, nunca de «no es sábado». */
  isWorkingDay: (key: string) => boolean;
}

/**
 * Tope de iteraciones del bucle, como red contra un bucle infinito por un dato corrupto.
 *
 * Generoso porque los pasos que caen en el pasado son aritmética pura y no tocan el calendario:
 * una tarea diaria cerrada dos años tarde consume ~730 pasos legítimos antes de llegar siquiera a
 * hoy. El límite de verdad, el que decide cuándo se deja de buscar, es `horizon`.
 */
const MAX_PASOS = 4000;

/** Cuánto hacia delante se mira antes de dar una serie por muerta. */
const DIAS_DE_HORIZONTE = 366;

/** `YYYY-MM-DD` → sus tres números. */
function partes(key: string): { y: number; m: number; d: number } {
  return {
    y: Number(key.slice(0, 4)),
    m: Number(key.slice(5, 7)),
    d: Number(key.slice(8, 10)),
  };
}

function clave(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function sumarDias(key: string, dias: number): string {
  const t = Date.parse(`${key}T00:00:00.000Z`) + dias * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Un mes más, aterrizando en `anclaDia` del mes de destino y recortando sólo cuando ese día no
 * existe ahí: con ancla 31, enero → 28 de febrero → **31 de marzo**, no 28 de marzo.
 *
 * El ancla viaja aparte precisamente para que el recorte sea local a un mes y no se quede
 * pegado a la serie.
 */
function sumarMeses(key: string, meses: number, anclaDia?: number): string {
  const { y, m, d } = partes(key);
  const total = y * 12 + (m - 1) + meses;
  const y2 = Math.floor(total / 12);
  const m2 = (total % 12) + 1;
  const ultimoDia = new Date(Date.UTC(y2, m2, 0)).getUTCDate();
  return clave(y2, m2, Math.min(anclaDia ?? d, ultimoDia));
}

function avanzar(key: string, rule: TaskRepeatRule, anclaDia?: number): string {
  switch (rule) {
    case 'DAILY_WORKDAY':
      return sumarDias(key, 1);
    case 'WEEKLY':
      return sumarDias(key, 7);
    case 'MONTHLY':
      return sumarMeses(key, 1, anclaDia);
  }
}

/** Cuántos días seguidos se admite correr una mensual buscando laborable antes de rendirse. */
const MAX_CORRIMIENTO = 31;

/**
 * Hasta qué día tiene que poder responder `isWorkingDay`, y por tanto qué rango de calendario hay
 * que cargar: `(today, horizon]`.
 *
 * Un año. No sale de ninguna cadencia — sale de que si una serie no encuentra un solo día
 * laborable en los próximos doce meses, está muerta y crear algo sería peor que no crear nada.
 *
 * Nótese que el rango NO empieza en `from`: los pasos anteriores a hoy se descartan por fecha sin
 * consultar el calendario, así que una tarea cerrada con dos años de retraso no obliga a cargar
 * dos años de festivos.
 */
export function recurrenceHorizon(today: string): string {
  return sumarDias(today, DIAS_DE_HORIZONTE);
}

/**
 * Dónde cae la siguiente ocurrencia, o por qué no cae en ningún sitio.
 *
 * 🚨 UN SOLO BUCLE RESUELVE LAS DOS DECISIONES DEL PROPIETARIO, y ése es el motivo de que esté
 * escrito así y no como dos casos:
 *
 *   1. «Si cae en festivo, no se crea esa semana» — se salta y se avanza a la siguiente válida.
 *      Saltar SIN avanzar mataría la serie: al generar al cerrar, si no nace nada no queda nada
 *      que cerrar y el primer festivo sería el último día de la serie.
 *   2. «Si la cierras tarde, nace en la primera fecha futura» — la del 6 de octubre cerrada el 3
 *      de noviembre da el 10 de noviembre, no el 13 de octubre. Mantiene la invariante de la que
 *      cuelga todo lo demás: SIEMPRE hay exactamente una viva, y está en el futuro. Con «+7 sobre
 *      la cerrada» nacería ya vencida, y al cerrarla nacería otra vencida: un atasco de tareas
 *      muertas que nadie va a hacer.
 *
 * Las dos son la misma frase — «avanza la cadencia hasta pasar de hoy y caer en día laborable» —
 * y por eso no hay dos ramas que puedan discrepar.
 */
export function nextOccurrenceDate(input: NextOccurrenceInput): NextOccurrence {
  const { from, rule, today, until, horizon, monthlyAnchorDay, isWorkingDay } = input;

  let cursor = from;
  for (let paso = 0; paso < MAX_PASOS; paso++) {
    cursor = avanzar(cursor, rule, monthlyAnchorDay);

    // El fin de serie se comprueba ANTES que el día laborable: una serie que acaba el 31 de
    // diciembre no debe seguir buscando en enero porque el 31 cayera en domingo.
    if (until !== null && cursor > until) return { kind: 'series-ended' };

    if (cursor <= today) continue;
    if (cursor > horizon) return { kind: 'no-valid-day' };

    if (isWorkingDay(cursor)) return { kind: 'next', date: cursor };

    // ─── El día cae en no laborable: y aquí MENSUAL se comporta distinto ───
    //
    // Diario y semanal SE SALTAN esa ocurrencia (decisión del propietario): la cadencia vuelve
    // en días o en una semana, así que perderse una es barato y la alternativa —correrla— iría
    // desplazando la serie.
    //
    // Mensual CORRE al siguiente laborable (decisión del propietario, 2026-10-04). Saltar un mes
    // entero porque el día cayó en sábado perdería la ocurrencia unos dos meses de cada siete, y
    // una tarea mensual que desaparece no se nota hasta que ya han pasado dos.
    //
    // Correr no desplaza la serie porque el mes siguiente se cuenta desde `monthlyAnchorDay`, no
    // desde la fecha corrida: el 15 en sábado nace el 17, y en octubre vuelve a ser el 15.
    if (rule !== 'MONTHLY') continue;

    let corrido = cursor;
    for (let dia = 0; dia < MAX_CORRIMIENTO; dia++) {
      corrido = sumarDias(corrido, 1);
      if (until !== null && corrido > until) return { kind: 'series-ended' };
      if (corrido > horizon) return { kind: 'no-valid-day' };
      if (isWorkingDay(corrido)) return { kind: 'next', date: corrido };
    }
    // Un mes entero sin un solo día laborable. No es un caso real; devolverlo como «me rindo»
    // deja rastro en el log en vez de seguir buscando en el mes siguiente con el ancla ya rota.
    return { kind: 'no-valid-day' };
  }

  return { kind: 'no-valid-day' };
}
